import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import classNames from 'classnames';
import { BASE_URL, IMAGE_PATHS } from '../../../../constants/paths.constants';
import { borderDarkGrey, disabledGrey, transparentPurple, universalStyles } from '../../../../app/theme';
import FindingsPanel from '../../../micro/FindingsPanel/FindingsPanel';

const useStyles = makeStyles(() => ({
  ...universalStyles,
  headerImage: {
    display: 'block',
    width: '100%',
  },
  pageSubtitle: {
    marginTop: 43,
    marginBottom: 25,
  },
  column: {
    width: '46%',
  },
  footnote: {
    fontSize: 14,
    color: borderDarkGrey,
    marginBottom: 30,
  },
  purpleBox: {
    margin: '0 25px 0 -25px',
    width: '100%',
    padding: '21px 25px',
    backgroundColor: transparentPurple,
    height: 'calc(100% - 42px)',
  },
  columnWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'stretch',
    '&:nth-of-type(3)': {
      marginBottom: 15,
    },
    '&:nth-of-type(4)': {
      marginBottom: 28,
    },
  },
  image: {
    maxWidth: '100%',
    height: 'auto',
  },
  rowTitle: {
    fontWeight: 'bold',
    marginBottom: 10,
  },
  text: {
    margin: '0 0 5px',
  },
  emphasis: {
    fontStyle: 'italic',
    color: disabledGrey,
    width: '100%',
    paddingBottom: 10,
    marginBottom: 25,
    borderBottom: `1px solid ${disabledGrey}`,
  },
}));

const InterpretationPage12 = (): JSX.Element => {
  const classes = useStyles();

  return (
    <>
      <div className={classes.grayBox}>
        <img
          className={classes.headerImage}
          src={`${BASE_URL}${IMAGE_PATHS.STEPS[1].INTERPRETATION_STEP_3.path}`}
          alt={`${IMAGE_PATHS.STEPS[1].INTERPRETATION_STEP_3.alt}`}
        />
        <div className={classes.whiteBox}>
          <Typography variant="h2">STEP 3:</Typography>
          <Typography variant="h3">Assess and Evaluate Axumin Uptake in Various Settings</Typography>
          <Typography variant="h5" className={classes.pageSubtitle}>
            Prostate Bed: Post-Prostatectomy
          </Typography>
          <img
            src={`${BASE_URL}${IMAGE_PATHS.PROSTATE_TABLE.path}`}
            alt={IMAGE_PATHS.PROSTATE_TABLE.alt}
            className={classes.image}
          />
          <Typography className={classes.footnote}>
            *Uptake not meeting threshold for malignancy (equivocal) may require follow-up and clinical correlation.
          </Typography>
          <ul className={classes.list}>
            <li>
              Uptake on anatomical correlate &lt; 1 cm, significantly greater than blood pool (ie, close to bone
              marrow), may also be considered suspicious for malignancy; MRI correlation is suggested
            </li>
            <ul className={classNames(classes.list, classes.nestedList)}>
              <li>
                If a lesion of this size does not meet this threshold, it should be reported as such but requires
                follow-up and clinical correlation
              </li>
            </ul>
            <li>Sagittal images are useful in the evaluation of the anastomotic site</li>
          </ul>
          <Typography variant="h5" className={classes.pageSubtitle}>
            Please review the images and findings below
          </Typography>
          <div className={classes.columnWrapper}>
            <div className={classes.column}>
              <Typography variant="body1" className={classes.text}>
                Subject presented with elevated PSA of 43.5 ng/mL
              </Typography>
              <Typography variant="body1" className={classNames(classes.text, classes.emphasis)}>
                See arrows for the location of the lesion and review findings
              </Typography>
            </div>
            <div className={classes.column}>
              <Typography variant="body1" className={classes.text}>
                Subject presented with elevated PSA of 0.29 ng/mL
              </Typography>
              <Typography variant="body1" className={classNames(classes.text, classes.emphasis)}>
                See arrows for the location of the lesion and review findings
              </Typography>
            </div>
          </div>
          <div className={classes.columnWrapper}>
            <div className={classes.column}>
              <div className={classes.purpleBox}>
                <Typography variant="body1" className={classes.rowTitle}>
                  Coronal view
                </Typography>
                <img
                  src={`${BASE_URL}${IMAGE_PATHS.ELEVATED_CORONAL.path}`}
                  alt={IMAGE_PATHS.ELEVATED_CORONAL.alt}
                  className={classes.image}
                />
              </div>
            </div>
            <div className={classes.column}>
              <div className={classes.purpleBox}>
                <Typography variant="body1" className={classes.rowTitle}>
                  Coronal view
                </Typography>
                <img
                  src={`${BASE_URL}${IMAGE_PATHS.ELEVATED_CORONAL_29.path}`}
                  alt={IMAGE_PATHS.ELEVATED_CORONAL_29.alt}
                  className={classes.image}
                />
              </div>
            </div>
          </div>
          <div className={classes.columnWrapper}>
            <div className={classes.column}>
              <div className={classes.purpleBox}>
                <Typography variant="body1" className={classes.rowTitle}>
                  Sagittal view
                </Typography>
                <img
                  src={`${BASE_URL}${IMAGE_PATHS.ELEVATED_SAGITAL.path}`}
                  alt={IMAGE_PATHS.ELEVATED_SAGITAL.alt}
                  className={classes.image}
                />
              </div>
            </div>
            <div className={classes.column}>
              <div className={classes.purpleBox}>
                <Typography variant="body1" className={classes.rowTitle}>
                  Sagittal view
                </Typography>
                <img
                  src={`${BASE_URL}${IMAGE_PATHS.SAGITAL_VIEW.path}`}
                  alt={IMAGE_PATHS.SAGITAL_VIEW.alt}
                  className={classes.image}
                />
              </div>
            </div>
          </div>
          <div className={classes.columnWrapper}>
            <div className={classes.column}>
              <Typography variant="body1" className={classes.rowTitle}>
                Findings:
              </Typography>
              <Typography variant="body1" className={classes.text}>
                Prostate bed recurrence detected
              </Typography>
            </div>
            <div className={classes.column}>
              <Typography variant="body1" className={classes.rowTitle}>
                Findings:
              </Typography>
              <Typography variant="body1" className={classes.text}>
                Recurrent prostate cancer detected in anastomotic area of bladder and urethra
              </Typography>
            </div>
          </div>
          <FindingsPanel trainingId={1} module="axuminInterpretation" page={12} />
        </div>
      </div>
    </>
  );
};

export default InterpretationPage12;
